/**
 * TurnstileWidget — wrapper React 19 para el widget Cloudflare Turnstile.
 *
 * - Si REACT_APP_TURNSTILE_SITE_KEY está vacío, muestra un placeholder y
 *   devuelve un token "dev-bypass" automáticamente (el backend lo acepta
 *   cuando TURNSTILE_SECRET_KEY también está vacío).
 * - Carga el script Turnstile bajo demanda (no se incluye en index.html).
 */
import { useEffect, useRef, useState, useCallback } from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export default function TurnstileWidget({ onToken, theme = "dark" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [scriptReady, setScriptReady] = useState(false);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

  // Dev mode: sin siteKey emite token bypass automáticamente
  useEffect(() => {
    if (!siteKey) {
      onToken?.("dev-bypass");
    }
  }, [siteKey, onToken]);

  // Carga script
  useEffect(() => {
    if (!siteKey) return;
    if (window.turnstile) { setScriptReady(true); return; }
    if (document.querySelector(`script[src^="https://challenges.cloudflare.com/turnstile"]`)) {
      // Esperar a que el script termine de cargar
      const i = setInterval(() => {
        if (window.turnstile) { clearInterval(i); setScriptReady(true); }
      }, 200);
      return () => clearInterval(i);
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => setScriptReady(true);
    document.body.appendChild(s);
  }, [siteKey]);

  // Render widget
  useEffect(() => {
    if (!scriptReady || !containerRef.current || widgetIdRef.current) return;
    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme,
        callback: (tok) => onToken?.(tok),
        "error-callback": () => onToken?.(""),
        "expired-callback": () => onToken?.(""),
      });
    } catch (e) {
      console.warn("[turnstile] render failed", e);
    }
  }, [scriptReady, siteKey, theme, onToken]);

  if (!siteKey) {
    return (
      <div className="text-[10px] text-[var(--ag-text-dim)] italic" data-testid="turnstile-disabled">
        (Verificación humana desactivada en este entorno)
      </div>
    );
  }
  return <div ref={containerRef} data-testid="turnstile-widget" />;
}
